import { createApp, ref } from "vue";
import Utils from "./utils/Utils";
import FlowOffers from "./flows/b2c/FlowOffers";
import FlowNoinvoice from "./flows/b2c/FlowNoinvoice";
import FlowInvoice from "./flows/b2c/FlowInvoice";
import FlowSme from "./flows/sme/FlowSme";
import { OfferCode, OfferTerm } from "./utils/OfferUtils";
import StepVoucher from "./steps/b2c/StepVoucher";
import StepVoucherOffers from "./steps/b2c/StepVoucherOffers";
import MaintenancePage from "./steps/MaintenancePage";
import Dialog from "./utils/Dialog";

interface AppOptions {
    step: string;
}

export interface OfferJson {
    id: number;
    type: string;
    tariff: string;
    title: string;
    fulltitle: string;

    period: number;
    price_warranty: number;

    prices: {
        price: number;
        price_netto: number;
        retail: number;
    }[];

    cashback: number;
    fee: number;
    feenext?: number;
    feenextmin?: number;
    savings: number;

    terms: OfferTerm[];
    terms_summary: OfferTerm[];

    alias: boolean;
    hidden: boolean;
}

export interface DocumentJson {
    name: string;
    size: number;
}

export interface BaseOrderJson {
    sme: boolean;

    usage: number;
    usage_original: number;
    tariff: string;

    offer: OfferJson;
    prevoffer_id: number;

    documents: DocumentJson[];

    fee?: number;
    fee_netto?: number;

    feenext?: number;
    feenext_netto?: number;

    fees?: {
        einvoice?: number;
        einvoice_netto?: number;
        trade?: number;
        trade_netto?: number;
    }

    phone: string;
    email: string;

    billing_active: boolean;
    billing_company: string;
    billing_nip: string;
    billing_firstname: string;
    billing_lastname: string;
    billing_street: string;
    billing_buildingno: string;
    billing_apartmentno: string;
    billing_zipcode: string;
    billing_city: string;

    other_active: boolean;
    other_street: string;
    other_buildingno: string;
    other_apartmentno: string;
    other_zipcode: string;
    other_city: string;

    ppe_active: boolean;
    ppe_street: string;
    ppe_buildingno: string;
    ppe_apartmentno: string;
    ppe_zipcode: string;
    ppe_city: string;

    ppe: string;

    terms: boolean;

    osd: number;
    prev_vendor: number;
    spare_vendor: number;

    period: number;
    noticeperiod: number;

    terms_trade_email: boolean;
    terms_trade_sms: boolean;
    terms_trade_phone: boolean;

    terms_trade_other_email: boolean;
    terms_trade_other_sms: boolean;
    terms_trade_other_phone: boolean;

    terms_einvoice: boolean;
    terms_contract: boolean;
    terms_notice: boolean;

    step: string;
    nextstep: string;

    complete: boolean;
    edited: boolean;
    verified: boolean;

    valid: boolean;

    captchakey?: string;
}

interface B2COrderJson extends BaseOrderJson {
    code: OfferCode;

    pesel: string;

    valid_invoice: boolean;
    valid_consents: boolean;
}

/** @deprecated */
export interface OrderJson extends B2COrderJson {
};

export enum CalcType {
    FLAT = 'flat',
    HOUSE = 'house',
}

export interface CalcJson {
    usage: number;
    size: number;
    type: CalcType;
}

export default class OrderForm {
    public static app;

    public static initApp($uid: JQuery): void {
        const config: AppOptions = $uid.data('options');

        OrderForm.app = createApp({
            components: {
                FlowOffers,
                FlowNoinvoice,
                FlowInvoice,
                FlowSme,
                MaintenancePage,
                StepVoucher,
                StepVoucherOffers,
                Dialog,
            },

            template: `
                <MaintenancePage
                    v-if="maintenance"
                />

                <div v-if="options" class="App">
                    <FlowOffers
                        v-if="isStep('offers*')"
                        :options="options"
                    />

                    <FlowNoinvoice
                        v-if="isStep('noinvoice*')"
                        :options="options"
                    />

                    <FlowInvoice
                        v-if="isStep('invoice*')"
                        :options="options"

                        @noinvoice="setStep('noinvoice')"
                    />

                    <FlowSme
                        v-if="isStep('sme*')"
                        :options="options"
                    />

                    <StepVoucher
                        v-if="isStep('voucher')"
                        :options="options"

                        @applyoptions="onCodeApply"
                    />

                    <StepVoucherOffers
                        v-if="isStep('voucher.offers')"
                        :options="options"

                        @offer:select="setStep('offers.details', options.offer)"
                    />

                    <Dialog
                        ref="exitdialog"
                        class="ExitDialog"

                        icon="stop"

                        :visible_header="false"
                    >
                        <p>
                            Jeśli teraz opuścisz stronę, <b>utracisz wprowadzone dane</b>. Czy na pewno chcesz to zrobić?
                        </p>

                        <button type="button" class="button button--primary button--full" @click="onExitCancel">Chcę kontynuować proces</button>
                        <button type="button" class="button button--secondary button--full" @click="onExitOk">Wychodzę</button>
                    </Dialog>
                </div>
            `,

            created() {
                this.reload();

                history.replaceState({
                    app_step: this.step,
                }, null, window.location.href);

                window.addEventListener('popstate', (e) => {
                    const step = e.state?.app_step;

                    if (step) {
                        this.step = step;

                        Utils.setCart('step', {
                            step: this.step,
                        });
                    }
                });

                setInterval(() => {
                    if (this.options.terms) {
                        Utils.setCart('ping', {
                            step: this.step,
                        }, false);
                    }
                }, 60 * 1000);
            },

            data() {
                return {
                    offer_revert: null,

                    step: config.step,
                };
            },

            methods: {
                isStep(val: string) {
                    if (this.maintenance) {
                        return false;
                    }

                    return Utils.isStep(val);
                },
            },

            setup(props, ctx) {
                const maintenance = ref(false);
                const options = ref(null as OrderJson);
                const dirtybypass = ref(false);
                const exitdialog = ref(null);

                async function reload() {
                    try {
                        const r = await Utils.post('/api/cart/init', {
                            config: config,
                        });

                        this.options = r.options;
                    } catch (e) {
                        window.location.href = '/';
                    }
                }

                function setStep(step: string, offer?: OfferJson) {
                    Utils.setStep(step, offer);
                }

                function onCodeApply(opts: OrderJson) {
                    setStep('voucher.offers');

                    options.value = opts;
                }

                $(window).on('beforeunload', (e: BeforeUnloadEvent) => {
                    if (options.value.terms && !dirtybypass.value) {
                        e.preventDefault();
                        e.returnValue = true;
                    }
                });

                let exit_res = null;

                function onExitOk() {
                    if (exit_res) {
                        exit_res(true);

                        (<any> exitdialog.value).close();
                    }
                }

                function onExitCancel() {
                    if (exit_res) {
                        exit_res(false);

                        (<any> exitdialog.value).close();
                    }
                }

                async function showExitDialog() {
                    return new Promise((res, err) => {
                        (<any> exitdialog.value).show();

                        exit_res = res;
                    });
                }

                $('.header, .footer').on('click', 'a', async (e) => {
                    if (options.value.terms) {
                        e.preventDefault();

                        dirtybypass.value = true;

                        const r = await showExitDialog();

                        if (r) {
                            window.location.href = $(e.currentTarget).prop('href');
                        }

                        dirtybypass.value = false;
                    }
                });

                return {
                    maintenance,
                    options,
                    exitdialog,

                    reload,
                    setStep,

                    onCodeApply,
                    onExitOk,
                    onExitCancel,
                }
            },
        }).mount($uid[0]);
    }
}